import { MessageBarType } from '@fluentui/react';
import { Button, buttonStylesGhost, buttonStylesPrimary, MessageBar, Panel, TextField } from '@h2oai/ui-kit';
import React, { useCallback, useState } from 'react';

import { IngressUpdateRequest } from '../../../../../admin-center/gen/fw_management/fw_management_pb';
import { useAdminCenterService } from '../../../../../admin-center/hooks';
import { useUser } from '../../../../../utils/hooks';
import { buildUserContext } from '../../../common/utils';
import { firewallInfoMessageStyles, sidePanelFooterStyle } from '../FWManagement.styles';
import { ParsedResponseMessages, parseResponseMessage } from '../FWManagement.utils';

interface IPPanelProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSubmitSuccess: () => Promise<void>;
}

export default function IPSidePanel({ isOpen, setOpen, onSubmitSuccess }: IPPanelProps) {
  const service = useAdminCenterService();
  const user = useUser();
  const [ipValue, setIPValue] = useState('');
  const [parsedMessages, setParsedMessages] = useState<ParsedResponseMessages | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const handleIPChange = (_event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setIPValue(newValue || '');
  };

  const handleIPFocus = () => {
    setParsedMessages(null);
    setErrorMessage(null);
  };

  const dismissPanel = useCallback(() => {
    setOpen(false);
    setParsedMessages(null);
    setErrorMessage(null);
  }, [setOpen]);
  const isFormValid = useCallback(() => {
    return ipValue !== null && ipValue !== undefined && ipValue !== '';
  }, [ipValue]);

  const handleSubmitClick = useCallback(async () => {
    if (!isFormValid()) return;

    const ipList: string[] = ipValue
      ? ipValue
          .split(',')
          .map((ip) => ip.trim())
          .filter((ip) => ip !== '')
      : [];

    try {
      const userContext = await buildUserContext(user);
      const request: IngressUpdateRequest = {
        newFwIps: ipList,
        modifiedBy: user?.name ?? '',
        timestamp: Date.now() / 1000,
        userContext: userContext,
      };
      const response = await service.updateIngressRules(request);
      if (response.statusUpdate) {
        const parsed = parseResponseMessage(response.statusUpdate);
        if (parsed) {
          setParsedMessages(parsed);
          setErrorMessage(null);
        } else {
          // Fallback to showing the raw message if parsing fails
          setErrorMessage(response.statusUpdate);
          setParsedMessages(null);
        }
      } else {
        setErrorMessage('Error: Failed to update');
        setParsedMessages(null);
      }
      await onSubmitSuccess();
    } catch (error) {
      const errorMsg = error instanceof Error && error.message ? error.message : 'An unknown error occurred';
      setErrorMessage(`Error: ${errorMsg}`);
      setParsedMessages(null);
    }
  }, [ipValue, onSubmitSuccess, isFormValid, user, service]);

  const onRenderFooterContent = useCallback(
    () => (
      <>
        <div style={sidePanelFooterStyle}>
          <Button styles={buttonStylesGhost} text="Cancel" onClick={dismissPanel} style={{ marginRight: 10 }} />
          <Button styles={buttonStylesPrimary} text="Add IP" onClick={handleSubmitClick} disabled={!isFormValid()} />
        </div>
      </>
    ),
    [dismissPanel, handleSubmitClick, isFormValid]
  );

  return (
    <Panel
      isLightDismiss
      isOpen={isOpen}
      onDismiss={dismissPanel}
      closeButtonAriaLabel="Close"
      isFooterAtBottom={true}
      onRenderFooterContent={onRenderFooterContent}
      customWidth="500px"
      type={7}
    >
      <h2>
        <strong>Add Inbound IP</strong>
      </h2>
      <h3>Grant access to your cloud environment</h3>
      <TextField
        label="Inbound IP(s)"
        placeholder="Add comma separated IPs. Ex: 176.230.223.145/32, 176.230.223.145/33"
        value={ipValue}
        onChange={handleIPChange}
        onFocus={handleIPFocus}
        required={true}
      />
      {parsedMessages && parsedMessages.added.length > 0 && (
        <MessageBar styles={firewallInfoMessageStyles} messageBarType={MessageBarType.success}>
          Successfully added: {parsedMessages.added.join(', ')}
        </MessageBar>
      )}
      {parsedMessages && parsedMessages.duplicates.length > 0 && (
        <MessageBar styles={firewallInfoMessageStyles} messageBarType={MessageBarType.warning}>
          Duplicates ignored: {parsedMessages.duplicates.join(', ')}
        </MessageBar>
      )}
      {parsedMessages && parsedMessages.invalid.length > 0 && (
        <MessageBar styles={firewallInfoMessageStyles} messageBarType={MessageBarType.error}>
          Invalid ignored: {parsedMessages.invalid.join(', ')}
        </MessageBar>
      )}
      {errorMessage && (
        <MessageBar styles={firewallInfoMessageStyles} messageBarType={MessageBarType.error}>
          {errorMessage}
        </MessageBar>
      )}
    </Panel>
  );
}
